/**
 * Meta Cloud API — Facebook SDK Configuration
 *
 * Uses FB.login() popup (Embedded Signup) instead of redirect-based OAuth.
 * The config_id comes from Meta's WhatsApp Embedded Signup configuration.
 *
 * META_APP_ID and META_CONFIG_ID are defined per environment
 * in the vite.config.{local|dev|prod}.ts `define` blocks,
 * following the same pattern as URL_API_PLICK, FIREBASE_API_KEY, etc.
 *
 * See: https://developers.facebook.com/docs/whatsapp/embedded-signup
 */

import type { EmbeddedSignupResult, EmbeddedSignupStatus, EmbeddedSignupSessionInfo } from './types';

/* eslint-disable no-var */
declare const META_APP_ID: string;
declare const META_CONFIG_ID: string;
/* eslint-enable no-var */

const META_SDK_VERSION = 'v25.0';

/**
 * `config_id` del Embedded Signup, re-exportado como valor runtime para que el hook
 * pueda adjuntarlo a Sentry (`extra.configId`) sin re-declarar el global de vite define.
 */
export const META_EMBEDDED_SIGNUP_CONFIG_ID = META_CONFIG_ID;

/**
 * Load the Facebook SDK asynchronously.
 * Idempotent: if already loaded, resolves immediately.
 * @returns {Promise<void>} Resolves when SDK is ready
 */
export const loadFacebookSdk = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.FB) {
      resolve();
      return;
    }

    // Init callback
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: false,
        version: META_SDK_VERSION,
      });
      resolve();
    };

    // Inject SDK script if not already present
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
      document.head.appendChild(script);
    }
  });
};

/**
 * Mensaje que Meta postea (`window.postMessage`) desde el popup de Embedded Signup.
 * Solo nos interesa `type === 'WA_EMBEDDED_SIGNUP'`.
 */
interface WaEmbeddedSignupMessage {
  type?: string;
  event?: EmbeddedSignupStatus;
  data?: {
    phone_number_id?: string;
    waba_id?: string;
    current_step?: string;
    error_message?: string;
  };
}

/**
 * Launch the Meta Embedded Signup popup via FB.login().
 *
 * Escucha en paralelo el `message` event de Meta (`WA_EMBEDDED_SIGNUP`), que es la única
 * fuente que distingue cancelar (CANCEL) de error (ERROR) y que trae `error_message`,
 * `current_step`, `phone_number_id` y `waba_id`. FB.login solo entrega el `code`.
 *
 * El resultado combina ambas fuentes y SIEMPRE remueve el listener al terminar (no fuga
 * entre reintentos). Solo rechaza si el SDK no está cargado; cancelar/error resuelven
 * con un `status` explícito para que el caller decida qué reportar.
 *
 * @returns {Promise<EmbeddedSignupResult>} Resultado estructurado del signup.
 */
export const launchEmbeddedSignup = (): Promise<EmbeddedSignupResult> => {
  return new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('Facebook SDK not loaded'));
      return;
    }

    // Última info recibida del popup vía postMessage. Se llena de forma asíncrona
    // mientras el comercio interactúa, y se consolida en el callback de FB.login.
    let sessionInfo: EmbeddedSignupSessionInfo | null = null;

    const handleMessage = (event: MessageEvent) => {
      // Solo aceptamos mensajes de Meta (cualquier subdominio de facebook.com).
      if (!event.origin || !event.origin.includes('facebook.com')) return;
      try {
        const payload = JSON.parse(event.data) as WaEmbeddedSignupMessage;
        if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
        sessionInfo = {
          event: payload.event,
          phoneNumberId: payload.data?.phone_number_id,
          wabaId: payload.data?.waba_id,
          currentStep: payload.data?.current_step,
          errorMessage: payload.data?.error_message,
        };
      } catch {
        // Otros frames de facebook.com postean mensajes no-JSON; los ignoramos.
      }
    };

    window.addEventListener('message', handleMessage);
    const cleanup = () => window.removeEventListener('message', handleMessage);

    const loginOptions = {
      config_id: META_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      // Coexistence: activa el flujo de "conectar tu número de la app WhatsApp Business"
      // (números previamente creados) en vez del onboarding estándar de número nuevo.
      // `sessionInfoVersion: '3'` hace que el popup emita el `message` event con
      // `waba_id`/`phone_number_id` que captura `handleMessage`.
      extras: {
        setup: {},
        featureType: 'whatsapp_business_app_onboarding',
        sessionInfoVersion: '3',
      },
    };

    // eslint-disable-next-line no-console
    console.log('[Meta Cloud API] FB.login options:', loginOptions);

    window.FB.login((response: { authResponse?: { code?: string } }) => {
      cleanup();

      // eslint-disable-next-line no-console
      console.log('[Meta Cloud API] FB.login response:', response);

      const code = response.authResponse?.code ?? null;

      // El `message` event manda. Si no llegó (p.ej. bloqueado), inferimos por el code:
      // hay code → FINISH (happy path preservado); sin code → CANCEL (cierre del popup).
      let status: EmbeddedSignupStatus;
      if (sessionInfo?.event) {
        status = sessionInfo.event;
      } else if (code) {
        status = 'FINISH';
      } else {
        status = 'CANCEL';
      }

      resolve({
        status,
        code,
        wabaId: sessionInfo?.wabaId,
        phoneNumberId: sessionInfo?.phoneNumberId,
        currentStep: sessionInfo?.currentStep,
        errorMessage: sessionInfo?.errorMessage,
      });
    }, loginOptions);
  });
};

// Extend window for Facebook SDK types
declare global {
  interface Window {
    FB: {
      init: (params: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
      login: (
        callback: (response: { authResponse?: { code?: string } }) => void,
        options: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          extras?: {
            setup?: Record<string, unknown>;
            featureType?: string;
            sessionInfoVersion?: string;
          };
        },
      ) => void;
    };
    fbAsyncInit: () => void;
  }
}
